import {
  EMAILS_FETCH_MORE_REQUESTED,
  EMAILS_FETCH_MORE_REQUEST_ERROR,
  EMAILS_FETCH_MORE_REQUEST_SUCCESS,
  EMAILS_FETCH_REQUESTED,
  EMAILS_FETCH_REQUEST_ERROR,
  EMAILS_FETCH_REQUEST_SUCCESS,
  EMAILS_MARKED_UPDATE_READ_STATUS_SUCCESS,
  EMAILS_SET_FILTERS,
  EMAILS_SET_MARKED_FOR_ACTION,
  EMAILS_UPDATE_SECTION,
  EMAIL_FETCH_REQUEST_ERROR,
  EMAIL_FETCH_REQUEST_SUCCESS,
  EMAIL_REMOVE_LABEL,
  EMAIL_RESET_CURRENT,
  EMAIL_RESET_MARKED_FOR_ACTION,
  EMAIL_SET_LABELS,
  EMAIL_SET_CURRENT,
  EMAILS_SET_HANDLED_BY_SUCCESS,
  EMAILS_FETCH_NEW_SILENTLY_SUCCESS,
  EMAIL_SET_SINGLE_LIST_ACTION_ID,
  EMAILS_REMOVE_HANDLED_BY_SUCCESS,
  EMAIL_DELETE_REQUESTED,
  EMAIL_DELETE_SUCCESS,
  EMAIL_DELETE_ERROR,
  EMAIL_DELETESTATUS_RESET,
  EMAIL_RESTORE_SUCCESS,
  EMAIL_FETCH_SILENT_SUCCESS,
  EMAILS_REMOVE_FROM_LIST,
  EMAILS_MARK_AS_REPLIED_OR_FORWARDED,
  EMAILS_FETCH_FOR_WIDGET,
  EMAILS_FETCH_STATISTICS_SUCCESS,
  EMAIL_UPDATE_DRAFT,
  EMAIL_FETCH_SEEN_BY_REQUESTED,
  EMAIL_FETCH_SEEN_BY_SUCCESS,
  EMAIL_FETCH_SEEN_BY_ERROR,
  EMAIL_FETCH_SEEN_BY_SUMMARY_SUCCESS,
  EMAIL_SEEN_BY_RESET,
  EMAIL_SEEN_BY_SUMMARY_RESET,
  EMAILS_HANDLE_JUNK_REQUESTED,
  EMAILS_HANDLE_JUNK_SUCCESS,
  EMAILS_HANDLE_JUNK_ERROR,
  EMAILS_HANDLE_JUNK_RESET,
} from './actionTypes'
import { merge, mergeAndConcat } from 'shared/utils/mergeObjects'
import { EmailActionTypes } from './types'
import {
  ParentRelationEnum,
  IEmailListItem,
  IEmailModel,
  IEmailSeenByUserSummary,
  IEmailSeenBy,
  JunkDestinationEnum,
} from 'Modules/Communication/Email/shared/models/Email'
import { ICommonListModel, ICommonSystemState } from 'shared/models/Common'
import { produce } from 'immer'
import {
  IDatesQueryModel,
  IInboxFilters,
  IInboxPatchSingle,
  InboxDeleteStatus,
  InboxSectionEnum,
  MessageState,
} from 'Modules/Communication/shared/models/Inbox'

const initialState = {
  data: null as ICommonListModel<IEmailListItem> | null,
  error: null,
  loading: false,
  loadingMore: false,
  updated: new Date(),
  filters: {
    searchText: '',
    queryStatus: InboxSectionEnum.Received,
    labelId: '',
    take: 20,
    skip: 0,
    onlyUnread: false,
    dates: {
      received: {
        from: undefined,
        to: undefined,
      },
      sent: {
        from: undefined,
        to: undefined,
      },
      deleted: {
        from: undefined,
        to: undefined,
      },
      processed: {
        from: undefined,
        to: undefined,
      },
    } as IDatesQueryModel,
  } as IInboxFilters,

  email: {
    current: null as string | null,
    data: null as IEmailModel | null,
    seenBy: {
      data: null,
      error: null,
      loading: false,
    } as ICommonSystemState<IEmailSeenBy>,

    seenBySummary: {
      messageId: null,
      readCount: 0,
    } as IEmailSeenByUserSummary,

    error: null,
    loading: false,
    completed: false,
  },

  marked: [] as string[],
  singleListActionId: null as string | null,
  deleteStatus: null as InboxDeleteStatus | null,
  unreadCount: null as number | null,
  junkmail: {
    processing: null as JunkDestinationEnum | null,
    error: null,
  },
  statistics: {
    unreadCount: null as number | null,
    sendingFailureCount: null as number | null,
  },
}

const reducer = (state = initialState, action: EmailActionTypes) => {
  switch (action.type) {
    case EMAILS_FETCH_FOR_WIDGET: {
      return produce(state, (draft) => {
        draft.loading = true
        draft.error = null
        draft.data = null
      })
    }

    case EMAILS_FETCH_REQUESTED:
      return produce(state, (draft) => {
        draft.loading = true
        draft.error = null
        draft.data = null
      })

    case EMAILS_FETCH_REQUEST_SUCCESS:
      return produce(state, (draft) => {
        draft.loading = false
        draft.data =
          action.payload as unknown as ICommonListModel<IEmailListItem>
        draft.error = null
        draft.updated = new Date()
      })

    case EMAILS_FETCH_REQUEST_ERROR:
      return produce(state, (draft) => {
        draft.loading = false
        draft.error = action.payload
      })

    case EMAILS_SET_FILTERS: {
      // Removed marked objects when swapping labels or changing searchTexts
      const shouldResetMarked = Object.keys(action.payload).some((item) =>
        ['labelId', 'queryStatus', 'searchText', 'onlyUnread'].includes(item)
      )

      const shouldResetOnlyUnread = Object.keys(action.payload).some((item) =>
        ['queryStatus'].includes(item)
      )

      return produce(state, (draft) => {
        const mergedFilters = merge(state.filters, action.payload, {
          searchText: action.payload.searchText || '',
        }) as typeof state.filters

        draft.filters = mergedFilters

        if (shouldResetOnlyUnread) {
          draft.filters.onlyUnread = false
        }

        draft.marked = shouldResetMarked ? [] : state.marked
        draft.filters.skip = 0
      })
    }

    case EMAILS_FETCH_MORE_REQUESTED:
      return produce(state, (draft) => {
        draft.loadingMore = true
        draft.filters.skip = state.filters.skip + 20
      })

    case EMAILS_FETCH_MORE_REQUEST_SUCCESS:
      return mergeAndConcat(state, {
        loadingMore: false,
        data: action.payload,
        updated: new Date(),
      })

    case EMAILS_FETCH_MORE_REQUEST_ERROR:
      return produce(state, (draft) => {
        draft.loadingMore = false
        draft.error = action.payload
      })

    case EMAILS_UPDATE_SECTION: {
      return produce(state, (draft) => {
        draft.filters.queryStatus = action.payload
        draft.filters.onlyUnread = false
        draft.filters.skip = 0
        draft.filters.dates = {
          received: {
            from: undefined,
            to: undefined,
          },
          sent: {
            from: undefined,
            to: undefined,
          },
          deleted: {
            from: undefined,
            to: undefined,
          },
          processed: {
            from: undefined,
            to: undefined,
          },
        }
        draft.marked = []
      })
    }

    case EMAILS_FETCH_NEW_SILENTLY_SUCCESS: {
      const incomingEmails = action.payload

      const existing = state.data as ICommonListModel<IEmailListItem> | null
      if (!existing || !Array.isArray(existing.result)) return state

      // Only show new incoming emails out of existing
      const newEmails = incomingEmails.filter(
        (email) => !existing.result.find((item) => item.id === email.id)
      )

      if (newEmails.length === 0) return state

      const newResult = [...newEmails, ...existing.result]

      return produce(state, (draft) => {
        draft.data = {
          result: newResult,
          totalCount: existing.totalCount + newEmails.length,
          totalPageCount: existing.totalPageCount,
          resultCount: existing.resultCount + newEmails.length,
          morePages: existing.morePages,
          currentPage: existing.currentPage,
        }
      })
    }

    case EMAILS_SET_MARKED_FOR_ACTION: {
      const id = action.payload

      return produce(state, (draft) => {
        if (state.marked.includes(id)) {
          draft.marked = state.marked.filter((item) => item !== id)
        } else {
          draft.email.current = null
          draft.email.seenBy.data = null
          draft.email.data = null
          draft.marked = [...state.marked, id]
        }
      })
    }

    case EMAIL_RESET_MARKED_FOR_ACTION: {
      return produce(state, (draft) => {
        draft.marked = []
      })
    }

    case EMAIL_SET_CURRENT: {
      return produce(state, (draft) => {
        draft.email.current = action.payload.id
        draft.email.completed = action.payload.id !== null ? false : true
        draft.email.data = null
        draft.email.error = null
        draft.email.loading = true
      })
    }

    case EMAIL_RESET_CURRENT: {
      return produce(state, (draft) => {
        draft.email.current = null
        draft.email.seenBy.data = null
        draft.email.completed = true
        draft.email.data = null
        draft.email.error = null
        draft.email.loading = false
      })
    }

    case EMAIL_FETCH_REQUEST_SUCCESS: {
      return produce(state, (draft) => {
        draft.email.completed = true
        draft.email.data = action.payload
        draft.email.error = null
        draft.email.loading = false
      })
    }

    case EMAIL_FETCH_REQUEST_ERROR: {
      return produce(state, (draft) => {
        draft.email.completed = false
        draft.email.data = null
        draft.email.error = action.payload ?? true
        draft.email.loading = false
      })
    }

    case EMAIL_FETCH_SILENT_SUCCESS: {
      return produce(state, (draft) => {
        if (!action.payload?.onlyFetchLabels)
          draft.email.data = action.payload.email
        else if (draft.email.data)
          draft.email.data.labels = action.payload.email?.labels || []
      })
    }

    case EMAILS_MARKED_UPDATE_READ_STATUS_SUCCESS: {
      const payload = action.payload as IInboxPatchSingle[]
      const typedData =
        state.data as unknown as ICommonListModel<IEmailListItem>
      const isMarkAsRead = payload[0]?.patchModel[0]?.value

      return produce(state, (draft) => {
        const resultUpdated = typedData?.result?.map((email) => {
          const patchItem = payload?.find((p) => p.id === email.id)
          if (patchItem) {
            email = {
              ...email,
              isRead: patchItem.patchModel[0].value || false,
            }
          }

          return email
        })

        const isCurrentEmail = payload.find(
          (item) => item.id === state.email.current
        )

        if (draft.data) {
          draft.data.result = resultUpdated || []
        }

        if (isCurrentEmail && draft.email.data) {
          draft.email.data.readDate = draft.email.data.readDate
            ? undefined
            : new Date()
        }
        if (draft.filters.queryStatus === InboxSectionEnum.Received)
          draft.statistics.unreadCount = isMarkAsRead
            ? (draft.statistics.unreadCount || 0) - payload.length
            : (draft.statistics.unreadCount || 0) + payload.length
      })
    }

    case EMAIL_SET_LABELS: {
      return produce(state, (draft) => {
        // Add the labels to the current email if it exists
        if (draft.email.data) {
          action.payload.forEach((label) => {
            if (!draft.email.data?.labels?.some((item) => item.id === label.id))
              draft.email.data?.labels?.push(label)
          })
        }

        // If we have marked emails, add the new labels to them if they dont already have them
        const listIds = draft.email.current
          ? [draft.email.current]
          : draft.marked

        listIds?.forEach((id) => {
          const email = draft.data?.result?.find((item) => item.id === id)

          if (email) {
            action.payload.forEach((newLabel) => {
              if (!email.labels?.some((item) => item.id === newLabel.id)) {
                email.labels?.push(newLabel)
              }
            })
          }
        })
      })
    }

    case EMAIL_REMOVE_LABEL: {
      return produce(state, (draft) => {
        const currentEmailId = state.email.current

        // If section is under labels, it will be moved to another folder
        // All = Labels/Etiketter
        if (draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.email.current = null
          draft.email.data = null

          // Remove current email from list
          if (draft.data?.result.length) {
            draft.data.result = draft.data?.result.filter(
              (item) => item.id !== currentEmailId
            )
          }
        } else {
          if (draft.email.data) {
            draft.email.data.labels = draft.email.data?.labels?.filter(
              (label) => label.id !== action.payload
            )
          }
        }

        const listItem = draft.data?.result?.find(
          (listItem) => listItem.id === currentEmailId
        )

        if (listItem)
          listItem.labels = listItem.labels.filter(
            (label) => label.id !== action.payload
          )
      })
    }

    case EMAILS_REMOVE_HANDLED_BY_SUCCESS: {
      return produce(state, (draft) => {
        if (
          draft.data?.result.length &&
          draft.filters.queryStatus !== InboxSectionEnum.All
        ) {
          draft.data.result = draft.data.result.filter(
            (item) => item.id !== action.payload
          )
        }

        if (draft.data && draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.data.result = draft.data.result.map((item) => {
            if (draft.email.data && action.payload === item.id) {
              item.state = String(draft.email.data.status) as MessageState
            }

            return item
          })
        }
      })
    }

    case EMAILS_SET_HANDLED_BY_SUCCESS: {
      const messageIds = action.payload.messageIds

      return produce(state, (draft) => {
        if (
          draft.filters.queryStatus !== InboxSectionEnum.Processed &&
          draft.filters.queryStatus !== InboxSectionEnum.All &&
          draft.data
        ) {
          draft.data.result = draft.data.result.filter(
            (item) => !messageIds.some((messageId) => messageId === item.id)
          )
          draft.marked = []
        }

        if (draft.data && draft.filters.queryStatus === InboxSectionEnum.All) {
          draft.data.result = draft.data.result.map((item) => {
            if (messageIds.includes(item.id)) {
              item.state = MessageState.Processed
            }

            return item
          })
        }
      })
    }

    case EMAIL_SET_SINGLE_LIST_ACTION_ID: {
      return produce(state, (draft) => {
        draft.singleListActionId = action.payload
      })
    }

    case EMAIL_DELETE_REQUESTED: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.isDeleting
      })
    }
    case EMAIL_DELETE_SUCCESS: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.success
        draft.marked = []

        if (draft.data) {
          // Only one email can be handled if we are in All (label section)
          if (draft.filters.queryStatus === InboxSectionEnum.All) {
            // Hard delete under chosen label
            if (draft.email.data?.state === MessageState.Deleted) {
              draft.data.result = draft.data.result.filter(
                (item) =>
                  !action.payload.some((deletedId) => deletedId === item.id)
              )
              // Soft delete under chosen label
            } else {
              draft.data.result = draft.data.result.map((item) => {
                if (action.payload.includes(item.id)) {
                  item.state = MessageState.Deleted
                }

                return item
              })
              // If this happens, currents gets set to null, and we dont need to care about the message itself
            }
          } else {
            draft.data.result = draft.data.result.filter(
              (item) =>
                !action.payload.some((deletedId) => deletedId === item.id)
            )
          }
        }
      })
    }
    case EMAIL_DELETE_ERROR: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.error
      })
    }
    case EMAIL_DELETESTATUS_RESET: {
      return produce(state, (draft) => {
        draft.deleteStatus = null
      })
    }

    case EMAIL_RESTORE_SUCCESS: {
      return produce(state, (draft) => {
        draft.deleteStatus = InboxDeleteStatus.success
        draft.marked = []

        if (draft.data) {
          if (draft.filters.queryStatus === InboxSectionEnum.All) {
            draft.data.result = draft.data.result.map((item) => {
              if (
                action.payload.includes(item.id) &&
                draft.email.data?.status
              ) {
                if (draft.email.data.processedDate) {
                  item.state = MessageState.Processed
                } else {
                  item.state = String(draft.email.data.status) as MessageState
                }
              }

              return item
            })
          } else {
            draft.data.result = draft.data.result.filter(
              (item) =>
                !action.payload.some((deletedId) => deletedId === item.id)
            )
          }
        }
      })
    }

    case EMAILS_REMOVE_FROM_LIST: {
      return produce(state, (draft) => {
        draft.marked = []
        if (draft.data) {
          draft.data.result = draft.data.result.filter(
            (item) => !action.payload.some((messageId) => messageId === item.id)
          )
        }
      })
    }

    case EMAILS_MARK_AS_REPLIED_OR_FORWARDED: {
      return produce(state, (draft) => {
        const emailInList = draft.data?.result?.find(
          (email) => email.id === action.payload.Relation?.MessageId
        )
        if (
          emailInList &&
          action.payload.Relation?.Relation === ParentRelationEnum.forward
        )
          emailInList.isForwarded = true
        else if (
          emailInList &&
          (action.payload.Relation?.Relation === ParentRelationEnum.reply ||
            action.payload.Relation?.Relation === ParentRelationEnum.replyAll)
        )
          emailInList.isRepliedOn = true
      })
    }

    case EMAILS_FETCH_STATISTICS_SUCCESS: {
      return produce(state, (draft) => {
        draft.statistics.unreadCount = action.payload.unreadCount || 0
        draft.statistics.sendingFailureCount =
          action.payload.sendingFailureCount || 0
      })
    }

    case EMAIL_UPDATE_DRAFT: {
      const id = action.payload.id
      return produce(state, (draft) => {
        // Update draft email in list and selected email if exists
        const emailInList = draft.data?.result?.find((x) => x.id === id)
        if (
          action.payload.field === 'contentHtml' &&
          draft.email?.data?.id === id
        )
          draft.email.data.contentHtml = action.payload.value
        else if (action.payload.field === 'title') {
          if (emailInList) emailInList.title = action.payload.value
          if (draft.email?.data?.id === id)
            draft.email.data.title = action.payload.value
        }
      })
    }

    case EMAIL_FETCH_SEEN_BY_SUMMARY_SUCCESS: {
      return produce(state, (draft) => {
        draft.email.seenBySummary = action.payload
      })
    }

    case EMAIL_FETCH_SEEN_BY_REQUESTED: {
      return produce(state, (draft) => {
        draft.email.seenBy = {
          data: null,
          loading: true,
          error: null,
        }
      })
    }

    case EMAIL_FETCH_SEEN_BY_SUCCESS: {
      return produce(state, (draft) => {
        draft.email.seenBy = {
          data: action.payload,
          loading: false,
          error: null,
        }
      })
    }

    case EMAIL_FETCH_SEEN_BY_ERROR: {
      return produce(state, (draft) => {
        draft.email.seenBy = {
          data: null,
          loading: false,
          error: action.payload,
        }
      })
    }

    case EMAIL_SEEN_BY_RESET: {
      return produce(state, (draft) => {
        draft.email.seenBy = {
          data: null,
          loading: false,
          error: null,
        }
      })
    }

    case EMAIL_SEEN_BY_SUMMARY_RESET: {
      return produce(state, (draft) => {
        draft.email.seenBySummary = {
          messageId: null,
          readCount: 0,
        }
      })
    }

    case EMAILS_HANDLE_JUNK_REQUESTED: {
      return produce(state, (draft) => {
        draft.junkmail.error = null
        draft.junkmail.processing = action.payload.destination
      })
    }

    case EMAILS_HANDLE_JUNK_SUCCESS: {
      return produce(state, (draft) => {
        draft.junkmail.error = null
        draft.junkmail.processing = null

        draft.email.data = null
        draft.email.current = null

        draft.marked = []
      })
    }

    case EMAILS_HANDLE_JUNK_ERROR: {
      return produce(state, (draft) => {
        draft.junkmail.error = action.payload
        draft.junkmail.processing = null
      })
    }

    case EMAILS_HANDLE_JUNK_RESET: {
      return produce(state, (draft) => {
        draft.junkmail.error = null
        draft.junkmail.processing = null
      })
    }

    default:
      return state
  }
}

export default reducer
